import { type SpaceProps } from "@chakra-ui/react";
import { BookDemoButton, SignupButton } from "components/event-tracking";
import { type DemoLinkParameters } from "hooks/use-demo-link";
import React, { useMemo } from "react";
import { ButtonGroup } from "ui/button";

type HeroCTAProps = {
  /**
   * Which button should act as the primary action.
   *
   * @default "demo"
   */
  primaryAction?: "demo" | "signup";

  /**
   * Override the text shown on the signup button.
   *
   * @default "Get started"
   */
  signupText?: string;

  /**
   * Override the text shown on the demo button.
   *
   * @default "Get a demo"
   */
  demoText?: string;

  /**
   * Instead of using the demo modal, use the demo button as a link to this path.
   */
  hasDemoLink?: boolean;

  /**
   * Custom parameters to use when building the demo link.
   */
  demoLinkParameters?: DemoLinkParameters;

  /**
   * Custom Chili Piper router to use with the demo modal.
   */
  chiliPiperRouterName?: string;
} & SpaceProps;

/**
 * Renders a button group containing the `BookDemoButton` and `SignupButton` components. The `primaryAction` will determine which action
 * renders as the `primary` style button on the right-hand side. The other action will render as a secondary button on the left-hand side.
 *
 * **Note:** if you only need one of the buttons in the hero, just use the `BookDemoButton` or `SignupButton` components directly.
 */
export function HeroCTA({
  primaryAction = "demo",
  signupText,
  demoText,
  hasDemoLink,
  demoLinkParameters,
  chiliPiperRouterName,
  ...spaceProps
}: HeroCTAProps) {
  const demoIsPrimary = primaryAction === "demo";

  const demoButton = useMemo(() => {
    return (
      <BookDemoButton
        variant={demoIsPrimary ? "primary" : "secondary"}
        text={demoText}
        isLink={hasDemoLink}
        demoLinkParameters={demoLinkParameters}
        chiliPiperRouterName={chiliPiperRouterName}
      />
    );
  }, [demoIsPrimary, demoText, hasDemoLink, demoLinkParameters, chiliPiperRouterName]);

  const signupButton = useMemo(() => {
    return <SignupButton variant={demoIsPrimary ? "secondary" : "primary"} text={signupText} />;
  }, [demoIsPrimary, signupText]);

  return (
    <ButtonGroup {...spaceProps}>
      {demoIsPrimary ? (
        <>
          {signupButton}
          {demoButton}
        </>
      ) : (
        <>
          {demoButton}
          {signupButton}
        </>
      )}
    </ButtonGroup>
  );
}
